import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useSuspenseQuery, type QueryClient } from "@tanstack/react-query";
import { AdminShell } from "@/components/layout/admin-shell";
import { splitName } from "@/lib/split-name";
import { appSidebarSections, appBottomNavItems } from "@/lib/app-sidebar";
import { IndexPageHeader, IndexPageHeaderTitle } from "@/components/vroom/index-page-header";
import { emailTemplatesListOptions } from "@/api/email-templates/query-options";
import { TemplateListTable } from "@/components/admin/email-templates/template-list-table";
import type { RoleKind } from "@/components/vroom/role-pill";

interface SessionUser {
  name: string;
  email: string;
  role?: RoleKind;
}

export const Route = createFileRoute("/_authed/admin/email-templates")({
  beforeLoad: ({ context }) => {
    const user = (context as { user?: { role?: string } }).user;
    if (user?.role !== "super_admin") throw redirect({ to: "/dashboard" });
  },
  loader: ({ context }) => {
    const qc = (context as { queryClient: QueryClient }).queryClient;
    void qc.prefetchQuery(emailTemplatesListOptions());
  },
  component: EmailTemplatesAdmin,
});

function EmailTemplatesAdmin() {
  const { t } = useTranslation();
  const { user: sessionUser } = Route.useRouteContext() as { user: SessionUser };
  const { firstName, lastName } = splitName(sessionUser.name);

  const { data } = useSuspenseQuery(emailTemplatesListOptions());

  return (
    <AdminShell
      user={{ firstName, lastName, role: "super_admin" }}
      section="admin"
      breadcrumb={<>{t("admin.email-templates.breadcrumb")}</>}
      sidebarSections={appSidebarSections({ active: "email-templates", role: "super_admin" }, t)}
      bottomNavItems={appBottomNavItems(
        { section: "admin", active: "email-templates", role: "super_admin" },
        t,
      )}
    >
      <IndexPageHeader>
        <IndexPageHeaderTitle title={t("admin.email-templates.title")} kicker="" />
      </IndexPageHeader>
      <div className="px-4 py-4 md:px-6 md:py-6">
        <p className="mb-6 text-sm text-ink-500">
          Pas per sjabloon de onderwerpregel en de inhoudsblokken aan per taal. Sjablonen zonder
          aanpassingen vallen terug op de ingebakken standaard.
        </p>
        <TemplateListTable items={data.items} />
      </div>
    </AdminShell>
  );
}
